import { auth } from './firebase/config';
import { ActivationCodeValidation, normalizeActivationCodeInput } from '../utils/activationCodes';

const parseJson = async (response: Response): Promise<any> => {
  try {
    return await response.json();
  } catch (_err) {
    return null;
  }
};

export interface ActivationRedeemResponse {
  success: boolean;
  message?: string;
  reason?: string;
  plan?: string;
  source?: string;
  campaignId?: string | null;
  codeId?: string;
}

export const validateActivationCode = async (code: string): Promise<ActivationCodeValidation> => {
  const normalized = normalizeActivationCodeInput(code);
  if (!normalized) {
    return { valid: false, reason: 'missing_code' };
  }

  const response = await fetch('/api/activation-codes/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: normalized }),
  });

  const data = await parseJson(response) || {};

  if (!response.ok) {
    return {
      valid: false,
      reason: data.reason || data.error || 'invalid_code',
    };
  }

  return {
    valid: !!data.valid,
    reason: data.reason,
    status: data.status,
    source: data.source,
    campaignId: data.campaignId ?? null,
    expiresAt: data.expiresAt || data.expires_at || null,
    codeId: data.codeId || data.code_id,
  };
};

export const redeemActivationCode = async (code: string): Promise<ActivationRedeemResponse> => {
  const normalized = normalizeActivationCodeInput(code);
  if (!normalized) {
    throw new Error('Activation code is required.');
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to redeem an activation code.');
  }

  const token = await user.getIdToken();
  const response = await fetch('/api/activation-codes/redeem', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ code: normalized }),
  });

  const data = await parseJson(response) || {};

  if (!response.ok || data.success === false) {
    throw new Error(data.message || data.reason || 'Unable to redeem activation code.');
  }

  return {
    success: true,
    message: data.message,
    reason: data.reason,
    plan: data.plan,
    source: data.source,
    campaignId: data.campaignId ?? null,
    codeId: data.codeId || data.code_id,
  };
};

